/**
 * Security Policy Types
 *
 * Firm-wide security policy configured on the Security Settings screen and
 * persisted via the security-service `/security-policies` endpoints. The wire
 * format is snake_case (see services/securityPolicy/mappers.ts); these are the
 * camelCase domain shapes the UI works with.
 */

export interface PasswordPolicy {
  minLength: number
  requireUppercase: boolean
  requireLowercase: boolean
  requireNumbers: boolean
  requireSpecialChars: boolean
  expiryDays: number
  preventReuse: number
}

export interface SessionPolicy {
  maxConcurrentSessions: number
  sessionTimeoutMinutes: number
  enforceIpBinding: boolean
  requireMfa: boolean
}

export interface LoginPolicy {
  maxFailedAttempts: number
  lockoutDurationMinutes: number
  allowRememberMe: boolean
}

export interface SecurityPolicy {
  passwordPolicy: PasswordPolicy
  sessionPolicy: SessionPolicy
  loginPolicy: LoginPolicy
}

/** Partial update — any subsection may be omitted. */
export interface UpdateSecurityPolicyInput {
  passwordPolicy?: PasswordPolicy
  sessionPolicy?: SessionPolicy
  loginPolicy?: LoginPolicy
}

/** Schema defaults — kept in sync with the backend SecurityPolicy schema. */
export const DEFAULT_SECURITY_POLICY: SecurityPolicy = {
  passwordPolicy: {
    minLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: false,
    expiryDays: 90,
    preventReuse: 5,
  },
  sessionPolicy: {
    maxConcurrentSessions: 5,
    sessionTimeoutMinutes: 60,
    enforceIpBinding: false,
    requireMfa: false,
  },
  loginPolicy: {
    maxFailedAttempts: 5,
    lockoutDurationMinutes: 30,
    allowRememberMe: true,
  },
}
